/**
 * MDITabs — Browser-like tab bar for multi-document interface
 *
 * **100% DSL-driven**: Icons resolved from shared icon-map registry.
 * Zero hardcoded icon imports.
 */

import { useRef, useState, useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import type { MdiTab } from '@/stores/mdi-store';
import { resolveIcon } from '@/lib/icon-map';

interface MDITabsProps {
  tabs: MdiTab[];
  activeTabId: string | null;
  onTabSelect: (tabId: string) => void;
  onTabClose: (tabId: string) => void;
  onCloseAll?: () => void;
}

export function MDITabs({ tabs, activeTabId, onTabSelect, onTabClose, onCloseAll }: MDITabsProps) {
  const { t } = useTranslation();
  const closeAllLabel = t('tabs.closeAll', 'Close all tabs');
  const allLabel = t('tabs.all', 'All');
  const scrollRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef(new Map<string, HTMLDivElement>());
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const pinnedTabs = tabs.filter((tab) => !tab.closable || tab.path === '/');
  const scrollTabs = tabs.filter((tab) => tab.closable && tab.path !== '/');

  const checkOverflow = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 1);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    checkOverflow();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', checkOverflow, { passive: true });
    const ro = new ResizeObserver(checkOverflow);
    ro.observe(el);
    return () => { el.removeEventListener('scroll', checkOverflow); ro.disconnect(); };
  }, [checkOverflow, tabs.length]);

  useEffect(() => {
    if (!activeTabId) return;
    const activeTabEl = tabRefs.current.get(activeTabId);
    if (!activeTabEl) return;
    activeTabEl.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    window.setTimeout(checkOverflow, 80);
  }, [activeTabId, checkOverflow]);

  const scroll = (dir: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir === 'left' ? -200 : 200, behavior: 'smooth' });
  };

  if (tabs.length === 0) return null;

  const displayTitleForTab = (tab: MdiTab) => {
    const [pathname, query = ''] = tab.path.split('?');
    if (pathname === '/wp/returns/form' || pathname === '/wm/returns/form') {
      return 'Material Return';
    }
    if (pathname === '/wp/returns/grn' || pathname === '/wm/returns/grn') {
      return 'Inventory Return';
    }
    if (pathname === '/wp/returns/project' || pathname === '/wm/returns/project') {
      return 'Inventory Transfer';
    }
    if (pathname === '/wp/inbound/new' || pathname === '/wm/inbound/new') {
      const mode = new URLSearchParams(query).get('mode');
      if (mode === 'RETURN') return 'Material Return';
      if (mode === 'PO') return 'Receive From PO';
    }
    return tab.title;
  };

  const renderTab = (tab: MdiTab) => {
    const isActive = tab.id === activeTabId;
    const Icon = resolveIcon(tab.icon ?? '');
    return (
      <div
        key={tab.id}
        ref={(node) => {
          if (node) tabRefs.current.set(tab.id, node);
          else tabRefs.current.delete(tab.id);
        }}
        className={cn(
          'group flex h-8 items-center gap-1.5 px-3 border-r cursor-pointer transition-colors select-none whitespace-nowrap',
          isActive
            ? 'bg-background border-t-2 border-t-primary rounded-t-md text-foreground'
            : 'bg-muted/50 hover:bg-muted text-muted-foreground border-t-2 border-t-transparent',
        )}
        onClick={() => onTabSelect(tab.id)}
      >
        <Icon className="h-3.5 w-3.5 shrink-0" />
        <span
          className={cn(
            'text-xs truncate max-w-[120px]',
            isActive && 'font-medium',
          )}
        >
          {displayTitleForTab(tab)}
        </span>
        {tab.closable && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onTabClose(tab.id);
            }}
            className={cn(
              'p-0.5 rounded hover:bg-accent transition-colors',
              isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
            )}
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="flex h-9 shrink-0 items-center border-b bg-muted/30">
      {pinnedTabs.length > 0 && (
        <div className="flex h-full shrink-0 items-end border-r bg-muted/40">
          {pinnedTabs.map(renderTab)}
        </div>
      )}

      {/* Scroll left — kept separate from tab close controls */}
      {canScrollLeft && (
        <button
          onClick={() => scroll('left')}
          title="Scroll tabs left"
          aria-label="Scroll tabs left"
          className="flex h-full w-8 shrink-0 items-center justify-center border-r text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <ChevronLeft className="h-3.5 w-3.5" />
        </button>
      )}

      {/* Scrollable tab area — hidden scrollbar */}
      <div
        ref={scrollRef}
        className="flex h-full min-w-0 flex-1 items-end overflow-x-auto scrollbar-none"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {scrollTabs.map(renderTab)}
      </div>

      {/* Scroll right — separate control, not overlaid on Close All */}
      {canScrollRight && (
        <button
          onClick={() => scroll('right')}
          title="Scroll tabs right"
          aria-label="Scroll tabs right"
          className="flex h-full w-8 shrink-0 items-center justify-center border-l text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
      )}

      {/* Close All button — distinct from scroll controls */}
      {onCloseAll && tabs.some((t) => t.closable) && (
        <button
          onClick={onCloseAll}
          title={closeAllLabel}
          aria-label={closeAllLabel}
          className="flex h-full shrink-0 items-center gap-1 border-l px-2.5 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
        >
          <X className="h-3.5 w-3.5" />
          <span className="hidden text-[11px] font-medium sm:inline">{allLabel}</span>
        </button>
      )}
    </div>
  );
}
